import React from 'react'
import { PureQueryOptions } from 'apollo-client'
import { Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import { ReviewMenu } from './review-menu'
import { ConfirmDenyChangeStatusDialog } from './confirm-deny-change-status-dialog'

interface DenyConfirmationManagerProps {
  readonly children: (props: {
    readonly onDenySelect: () => void
  }) => React.ReactNode
  readonly onDeny: (comment: string) => Promise<any>
  readonly status?: string
}

class DenyConfirmationManager extends React.Component<
  DenyConfirmationManagerProps,
  { readonly isOpen: boolean }
> {
  public state = {
    isOpen: false
  }

  public render() {
    return (
      <React.Fragment>
        {this.props.children({ onDenySelect: this.onDenySelect })}
        {this.state.isOpen && (
          <ConfirmDenyChangeStatusDialog
            isOpen={true}
            onDeny={this.onDeny}
            onCancel={this.onDenyCancel}
            status={this.props.status}
          />
        )}
      </React.Fragment>
    )
  }

  private onDenySelect = () => this.setState({ isOpen: true })

  private onDeny = (comment: string) => {
    this.setState({ isOpen: false })
    return this.props.onDeny(comment)
  }

  private onDenyCancel = () => this.setState({ isOpen: false })
}

interface Vulnerability {
  readonly id: string
}

interface Response {
  readonly _placeholder: boolean
}

interface ApproveWithIdsVars {
  readonly organizationID: string
  readonly ids: string[]
}

interface DenyWithIdsVars extends ApproveWithIdsVars {
  readonly comment: string
}

interface ReviewWithIDsProps {
  readonly vulnerabilities: Vulnerability[]
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
  readonly menuPlacement: 'top' | 'bottom'
}

export const approveStatusByIdsMutation = gql`
  mutation ApproveStatusByIds($organizationID: ID!, $ids: [ID!]!) {
    approveVulnerabilitiesStatus(
      input: { organizationID: $organizationID, ids: $ids }
    ) {
      _placeholder
    }
  }
`

export const denyStatusByIdsMutation = gql`
  mutation DenyStatusByIds(
    $organizationID: ID!
    $ids: [ID!]!
    $comment: String!
  ) {
    denyVulnerabilitiesStatus(
      input: { organizationID: $organizationID, ids: $ids, comment: $comment }
    ) {
      _placeholder
    }
  }
`

export function ReviewWithIDsAction({
  organizationID,
  refresh,
  refetchQueries,
  vulnerabilities,
  menuPlacement
}: ReviewWithIDsProps) {
  return (
    <Mutation<Response, ApproveWithIdsVars>
      mutation={approveStatusByIdsMutation}
    >
      {(approve) => (
        <Mutation<Response, DenyWithIdsVars> mutation={denyStatusByIdsMutation}>
          {(deny) => (
            <DenyConfirmationManager
              onDeny={(comment) =>
                deny({
                  variables: {
                    organizationID,
                    ids: vulnerabilities.map((v) => v.id),
                    comment
                  },
                  refetchQueries
                }).then(refresh)
              }
            >
              {({ onDenySelect }) => {
                return (
                  <ReviewMenu
                    onApprove={() =>
                      approve({
                        variables: {
                          organizationID,
                          ids: vulnerabilities.map((v) => v.id)
                        },
                        refetchQueries
                      }).then(refresh)
                    }
                    onDeny={onDenySelect}
                    menuPlacement={menuPlacement}
                  />
                )
              }}
            </DenyConfirmationManager>
          )}
        </Mutation>
      )}
    </Mutation>
  )
}

interface ApproveWithFiltersVars<TAggregations extends string> {
  readonly excludedVulnerabilities: string[]
  readonly organizationID: string
  readonly filters: Record<TAggregations, string[]> & {
    readonly filterText: string
    readonly quickFilter: string
  }
}

interface DenyWithFiltersVars<TAggregations extends string>
  extends ApproveWithFiltersVars<TAggregations> {
  readonly comment: string
}

interface ReviewWithFiltersProps<TAggregationCategories extends string> {
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly excludedVulnerabilities: Vulnerability[]
  readonly refresh: () => Promise<any>
  readonly filters: {
    readonly filterText: string
    readonly quickFilter: string
    readonly aggregations: Record<TAggregationCategories, string[]>
  }
  readonly additionalFilters?: { readonly [key: string]: any }
  readonly menuPlacement: 'top' | 'bottom'
}

export const approveStatusByFiltersMutation = gql`
  mutation ApproveStatusByFilters(
    $organizationID: ID!
    $filters: VulnerabilityFilterArg!
    $excludedVulnerabilities: [ID!]!
  ) {
    bulkApproveVulnerabilitiesStatus(
      input: {
        organizationID: $organizationID
        filters: $filters
        excludedVulnerabilities: $excludedVulnerabilities
      }
    ) {
      _placeholder
    }
  }
`

export const denyStatusByFiltersMutation = gql`
  mutation DenyStatusByFilters(
    $organizationID: ID!
    $filters: VulnerabilityFilterArg!
    $excludedVulnerabilities: [ID!]!
    $comment: String!
  ) {
    bulkDenyVulnerabilitiesStatus(
      input: {
        organizationID: $organizationID
        filters: $filters
        excludedVulnerabilities: $excludedVulnerabilities
        comment: $comment
      }
    ) {
      _placeholder
    }
  }
`

export function ReviewWithFiltersAction<TAggregationCategories extends string>({
  organizationID,
  refresh,
  refetchQueries,
  excludedVulnerabilities,
  filters: { aggregations, quickFilter, filterText },
  additionalFilters,
  menuPlacement
}: ReviewWithFiltersProps<TAggregationCategories>) {
  return (
    <Mutation<Response, ApproveWithFiltersVars<TAggregationCategories>>
      mutation={approveStatusByFiltersMutation}
    >
      {(approve) => (
        <Mutation<Response, DenyWithFiltersVars<TAggregationCategories>>
          mutation={denyStatusByFiltersMutation}
        >
          {(deny) => (
            <DenyConfirmationManager
              onDeny={(comment) =>
                deny({
                  variables: {
                    organizationID,
                    excludedVulnerabilities: excludedVulnerabilities.map(
                      (v) => v.id
                    ),
                    filters: Object.assign({}, aggregations, {
                      quickFilter,
                      filterText,
                      ...additionalFilters
                    }),
                    comment
                  },
                  refetchQueries
                }).then(refresh)
              }
            >
              {({ onDenySelect }) => {
                return (
                  <ReviewMenu
                    onApprove={() =>
                      approve({
                        variables: {
                          organizationID,
                          excludedVulnerabilities: excludedVulnerabilities.map(
                            (v) => v.id
                          ),
                          filters: Object.assign({}, aggregations, {
                            quickFilter,
                            filterText,
                            ...additionalFilters
                          })
                        },
                        refetchQueries
                      }).then(refresh)
                    }
                    onDeny={onDenySelect}
                    menuPlacement={menuPlacement}
                  />
                )
              }}
            </DenyConfirmationManager>
          )}
        </Mutation>
      )}
    </Mutation>
  )
}
